import { assertDefined, isDefined } from '../../../lib/util';
import { type ClientId } from '../../core/ClientId';
import { isCargoEventTimeData, type TimeData } from '../../core/TimeData';
import {
  getImosRobByGrade,
  type ImosRobByGrade,
  type VesonBunker,
} from '../../integrations/VesonRob';
import {
  createDependencyAccessor,
  type PnlProcessorContext,
} from '../context/context';
import { PnlProcessor, type PnlProcessorResult } from '../processor';
import { type BunkerCost } from './bunkerCost';
import { type BunkerConsumption } from './bunkerConsumption';
import { getCostSharingContext } from '../costSharing/costSharing';

const PROCESSOR_METADATA = {
  id: 'bunker-cost',
  dependencies: ['cost-sharing'],
};

interface BunkerConsumptionContext {
  consumptionByTd: Map<TimeData, BunkerConsumption[]>;
}

export const getBunkerConsumptionContext =
  createDependencyAccessor<BunkerConsumptionContext>(PROCESSOR_METADATA.id);

function getTimeDataExternalShipmentId(td: TimeData): string | undefined {
  if (!isCargoEventTimeData(td)) return undefined;
  const externalShipmentId = td.event.order.shipment?.externalShipmentId;
  return externalShipmentId ?? undefined;
}

export type BunkerCostProcessorInput = {
  imosBunkerData?: VesonBunker;
};

type BunkerCostProcessorResult = PnlProcessorResult<BunkerCost>;

export class BunkerCostProcessor extends PnlProcessor<BunkerCost> {
  private state: {
    rob: {
      imosDefaultPriceByGrade: Map<string, number>;
      imosRobData: VesonBunker | undefined;
      imosRobByShipmentId: Map<ClientId, ImosRobByGrade | undefined>;
      /** External id of previous shipment that had ROB available */
      previousShipmentWithRob: string | null;
    };
  } = {
    rob: {
      imosDefaultPriceByGrade: new Map(),
      imosRobData: undefined,
      imosRobByShipmentId: new Map(),
      previousShipmentWithRob: null,
    },
  };

  public result: BunkerCostProcessorResult = {
    costs: [],
    errors: [],
    income: undefined,
  };

  constructor(input: BunkerCostProcessorInput, context: PnlProcessorContext) {
    super(PROCESSOR_METADATA, context);

    this.state.rob.imosRobData = input.imosBunkerData;

    const imosDefaultPrices =
      context.type === 'vessel' && context.vessel?.shortName
        ? input.imosBunkerData?.defaultBunkerByVesselCode.get(
            context.vessel.shortName,
          )
        : undefined;

    if (imosDefaultPrices) {
      for (const defaultPrice of imosDefaultPrices) {
        this.state.rob.imosDefaultPriceByGrade.set(
          defaultPrice.name,
          defaultPrice.price,
        );
      }
    }

    context.shared[PROCESSOR_METADATA.id] = {
      consumptionByTd: new Map(),
    };
  }

  processEvent(td: TimeData): void {
    this.updateImosRob(td);
    this.calculateCostAndConsumptionForEvent(td);
  }
  finalize(): void {}

  getResult() {
    return this.result;
  }

  // TODO: Voyage calculation api, needs to also use ROB?
  calculateCostAndConsumptionForEvent(td: TimeData) {
    const consumptions = extractTimeDataConsumptions(td);

    for (const consumption of consumptions) {
      const { bunkerCostPerTonne, bunkerGrade, consumedBunkerTonnes } =
        consumption;
      let amountToConsume = consumedBunkerTonnes;

      const shipmentId =
        consumption.type === 'LADEN' || consumption.type === 'BALLAST'
          ? this.getLegShipmentId(td)
          : this.getTimeDataShipmentId(td);

      // Use ROB fuel if available
      const robConsumption = shipmentId
        ? this.consumeFromRob(shipmentId, bunkerGrade ?? '', amountToConsume)
        : undefined;

      amountToConsume = robConsumption?.remainingToConsume ?? amountToConsume;
      const cost =
        amountToConsume * bunkerCostPerTonne + (robConsumption?.totalCost ?? 0);

      this.result.costs.push({
        type: 'cost',
        category: 'bunker_cost',
        bunkerGrade,
        consumption,
        amount: cost,
        portCallId: td.event.portCallId,
        pricePerTonne: bunkerCostPerTonne,
        robConsumedTonnes: robConsumption?.robConsumed ?? 0,
        shipmentId: shipmentId,
      });
    }

    this.updateSharedContext(td, consumptions);
  }

  private getSharedContext(): BunkerConsumptionContext {
    return this.context.shared[
      PROCESSOR_METADATA.id
    ] as BunkerConsumptionContext;
  }

  private updateSharedContext(td: TimeData, consumptions: BunkerConsumption[]) {
    const context = this.getSharedContext();
    context.consumptionByTd.set(td, consumptions);
  }

  /**
   * Consume fuel from the bunker ROB (Remaining Onboard) for a given shipment.
   */
  private consumeFromRob(
    shipmentId: ClientId,
    /** Grade name of the bunker */
    gradeName: string,
    /** Amount of fuel to consume in tonnes */
    amountToConsume: number,
  ) {
    const robByGrade = this.state.rob.imosRobByShipmentId.get(shipmentId);
    const defaultPrice = this.state.rob.imosDefaultPriceByGrade.get(gradeName);
    const robEntries = robByGrade?.get(gradeName) ?? [];
    const lastRobPrice = robEntries[0]?.price;
    let remainingToConsume = amountToConsume;
    let totalCost = 0;

    for (let i = robEntries.length - 1; i >= 0 && remainingToConsume > 0; i--) {
      const entry = robEntries[i];
      const consumeFromBatch = Math.min(remainingToConsume, entry.amount);

      totalCost += consumeFromBatch * entry.price;
      remainingToConsume -= consumeFromBatch;

      // Update entry with remaining bunker amount
      entry.amount = Math.max(entry.amount - consumeFromBatch, 0);
    }

    // TODO: How do we want to report price when rob is used? Is average price
    // enough or do we want the splits to be visible in UI?
    //
    // Calculate effective rate if any fuel was consumed
    // const averageRobPrice =
    //   consumedAmount > 0 ? totalCost / (amountToConsume - remainingToConsume) : null;

    const robConsumed = amountToConsume - remainingToConsume;

    // If there is consumption remaining, use the last price or default price if
    // no rob was found for this shipment
    const remainingConsumptionPrice = lastRobPrice || defaultPrice;
    if (remainingToConsume > 0 && isDefined(remainingConsumptionPrice)) {
      totalCost += remainingToConsume * remainingConsumptionPrice;
      remainingToConsume = 0;
    }

    return {
      remainingToConsume,
      robConsumed,
      totalCost,
    };
  }

  private getTimeDataShipmentId(td: TimeData) {
    const { costSharingDetailsByTd } = getCostSharingContext(this.context);
    const costSharingDetails = costSharingDetailsByTd.get(td);
    assertDefined(
      costSharingDetails,
      'cost sharing details expected to be available',
    );

    return costSharingDetails.eventShipmentId;
  }

  private getLegShipmentId(td: TimeData) {
    const { costSharingDetailsByTd } = getCostSharingContext(this.context);
    const costSharingDetails = costSharingDetailsByTd.get(td);
    assertDefined(
      costSharingDetails,
      'cost sharing details expected to be available',
    );

    return costSharingDetails.legShipmentId;
  }

  /** Adds IMOS Rob to shipment's remaining bunkers */
  private updateImosRob(td: TimeData) {
    if (!this.state.rob.imosRobData) return;
    const { remainingBunkerByExternalShipmentId } = this.state.rob.imosRobData;

    const shipmentId = this.getTimeDataShipmentId(td);

    const externalShipmentId = getTimeDataExternalShipmentId(td);
    if (!shipmentId || !externalShipmentId) return;

    if (this.state.rob.imosRobByShipmentId.has(shipmentId)) {
      return;
    }

    const remainingBunkerOfVoyage = externalShipmentId
      ? remainingBunkerByExternalShipmentId.get(externalShipmentId)
      : undefined;

    if (remainingBunkerOfVoyage) {
      const robByGrade = getImosRobByGrade(remainingBunkerOfVoyage.bunkerOpen);
      this.state.rob.imosRobByShipmentId.set(shipmentId, robByGrade);
      this.state.rob.previousShipmentWithRob = externalShipmentId;
      return;
    }

    // Add entry for missing ROB so we don't need to check it again
    this.state.rob.imosRobByShipmentId.set(shipmentId, undefined);

    // If no IMOS Rob data for the current shipment, use `bunkerClose` from
    // the previous shipment if available
    const previousExternalShipmentId = this.state.rob.previousShipmentWithRob;

    if (!previousExternalShipmentId) {
      return;
    }

    const remainingBunkerOfPreviousShipment =
      remainingBunkerByExternalShipmentId.get(previousExternalShipmentId);

    if (remainingBunkerOfPreviousShipment) {
      const robByGrade = getImosRobByGrade(
        remainingBunkerOfPreviousShipment.bunkerClose,
      );

      this.state.rob.imosRobByShipmentId.set(shipmentId, robByGrade);
    }

    // Only use ROB from the previous available shipment once
    this.state.rob.previousShipmentWithRob = null;
  }
}

function extractTimeDataConsumptions(td: TimeData): BunkerConsumption[] {
  const { extraConsumptionData, insideEcaData, portData, outsideEcaData } =
    td.costs.bunkerGrade;

  const consumptions: BunkerConsumption[] = [];

  if (insideEcaData) {
    consumptions.push({
      type: td.laden ? 'LADEN' : 'BALLAST',
      bunkerGrade: insideEcaData.gradeName ?? insideEcaData.referenceGrade,
      bunkerGradeId: insideEcaData.gradeId,
      bunkerCostPerTonne: insideEcaData.fuelCost,
      insideEca: true,
      consumptionTonnesPerDay: insideEcaData.consumption,
      consumedBunkerTonnes: insideEcaData.consumedBunker,
    });
  }

  if (outsideEcaData) {
    consumptions.push({
      type: td.laden ? 'LADEN' : 'BALLAST',
      bunkerGrade: outsideEcaData.gradeName ?? outsideEcaData.referenceGrade,
      bunkerGradeId: outsideEcaData.gradeId,
      bunkerCostPerTonne: outsideEcaData.fuelCost,
      insideEca: false,
      consumptionTonnesPerDay: outsideEcaData.consumption,
      consumedBunkerTonnes: outsideEcaData.consumedBunker,
    });
  }

  if (portData) {
    consumptions.push({
      type: 'PORT',
      bunkerGrade: portData.gradeName || portData.referenceGrade,
      bunkerGradeId: portData.gradeId,
      bunkerCostPerTonne: portData.fuelCost,
      insideEca: false,
      consumptionTonnesPerDay: portData.consumption,
      consumedBunkerTonnes: portData.consumedBunker,
    });
  }

  if (extraConsumptionData) {
    consumptions.push({
      type: 'PORT',
      bunkerGrade:
        extraConsumptionData.gradeName || extraConsumptionData.referenceGrade,
      bunkerGradeId: extraConsumptionData.gradeId,
      bunkerCostPerTonne: extraConsumptionData.fuelCost,
      insideEca: false,
      consumptionTonnesPerDay: extraConsumptionData.consumption,
      consumedBunkerTonnes: extraConsumptionData.consumedBunker,
    });
  }

  return consumptions;
}
